import { useQuery, type UseMutationResult, type UseQueryResult } from "@tanstack/react-query";
import { useWorkspaceKeys, type WorkspaceKeys } from "../../app/current-user.tsx";
import { researchApi } from "../../client.ts";
import type {
  AgreementReport,
  AutonomyInput,
  Calibration,
  CalibrationInput,
  CreatedRunnerKey,
  EvaluationQueued,
  GroupAutonomy,
  PipelineOverview,
  PipelineStep,
  PipelineStepInput,
  PipelineStepUpdate,
  RunnerKey,
  RunnerKeyInput,
} from "../../domain/imaging.ts";
import type {
  BenchmarkGateInput,
  BenchmarkInput,
  BenchmarkQueued,
  BenchmarkReport,
  ModelVersion,
  ModelVersionInput,
  ModelVersionsOverview,
  ModelVersionUpdate,
} from "../../domain/lab-models.ts";
import type { AnalysisGroup } from "../../domain/schema.ts";
import { useWorkspaceMutation } from "../../lib/workspace-mutation.ts";
import { workspaceQuery, type WorkspaceQuery } from "../../lib/workspace-query.ts";

// Lab writes fill no record: the workspace refresh reloads the lists, steps and autonomy they touch.

export const calibrationsQuery = (keys: WorkspaceKeys): WorkspaceQuery<Calibration[]> =>
  workspaceQuery([...keys.all, "calibrations"], () => researchApi.admin.listCalibrations());

export const runnerKeysQuery = (keys: WorkspaceKeys): WorkspaceQuery<RunnerKey[]> => ({
  ...workspaceQuery([...keys.all, "runner-keys"], () => researchApi.admin.listRunnerKeys()),
  refetchOnWindowFocus: "always",
});

export const pipelineQuery = (keys: WorkspaceKeys): WorkspaceQuery<PipelineOverview> => ({
  ...workspaceQuery([...keys.all, "pipeline"], () => researchApi.admin.getPipeline()),
  refetchOnWindowFocus: "always",
});

const agreementQuery = (
  keys: WorkspaceKeys,
  group: AnalysisGroup,
): WorkspaceQuery<AgreementReport> =>
  workspaceQuery([...keys.all, "agreement", group], () => researchApi.admin.getAgreement(group));

export const useCalibrations = (): UseQueryResult<Calibration[]> =>
  useQuery(calibrationsQuery(useWorkspaceKeys()));

export const useCreateCalibration = (): UseMutationResult<Calibration, Error, CalibrationInput> =>
  useWorkspaceMutation({
    mutationFn: (input: CalibrationInput) => researchApi.admin.createCalibration(input),
  });

export const useRetireCalibration = (): UseMutationResult<Calibration, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.admin.retireCalibration(id),
  });

export const useRunnerKeys = (): UseQueryResult<RunnerKey[]> =>
  useQuery(runnerKeysQuery(useWorkspaceKeys()));

/** The answer is the only time the secret is shown. */
export const useCreateRunnerKey = (): UseMutationResult<CreatedRunnerKey, Error, RunnerKeyInput> =>
  useWorkspaceMutation({
    mutationFn: (input: RunnerKeyInput) => researchApi.admin.createRunnerKey(input),
  });

export interface RunnerKeyUpdate {
  id: string;
  input: RunnerKeyInput;
}

export const useUpdateRunnerKey = (): UseMutationResult<RunnerKey, Error, RunnerKeyUpdate> =>
  useWorkspaceMutation({
    mutationFn: ({ id, input }: RunnerKeyUpdate) => researchApi.admin.updateRunnerKey(id, input),
  });

export const useRevokeRunnerKey = (): UseMutationResult<RunnerKey, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.admin.revokeRunnerKey(id),
  });

export const usePipeline = (): UseQueryResult<PipelineOverview> =>
  useQuery(pipelineQuery(useWorkspaceKeys()));

export const useCreatePipelineStep = (): UseMutationResult<
  PipelineStep,
  Error,
  PipelineStepInput
> =>
  useWorkspaceMutation({
    mutationFn: (input: PipelineStepInput) => researchApi.admin.createPipelineStep(input),
  });

export interface PipelineStepChange {
  id: string;
  input: PipelineStepUpdate;
}

export const useUpdatePipelineStep = (): UseMutationResult<
  PipelineStep,
  Error,
  PipelineStepChange
> =>
  useWorkspaceMutation({
    mutationFn: ({ id, input }: PipelineStepChange) =>
      researchApi.admin.updatePipelineStep(id, input),
  });

/** Queues evaluation jobs on the group's reference photos; the gate reads their results. */
export const useEvaluatePipelineStep = (): UseMutationResult<EvaluationQueued, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.admin.evaluatePipelineStep(id),
  });

export const useGatePipelineStep = (): UseMutationResult<PipelineStep, Error, string> =>
  useWorkspaceMutation({
    mutationFn: (id: string) => researchApi.admin.gatePipelineStep(id),
  });

export interface AutonomyChange {
  group: AnalysisGroup;
  input: AutonomyInput;
}

export const useSetAutonomy = (): UseMutationResult<GroupAutonomy, Error, AutonomyChange> =>
  useWorkspaceMutation({
    mutationFn: ({ group, input }: AutonomyChange) => researchApi.admin.setAutonomy(group, input),
  });

export const useAgreement = (group: AnalysisGroup): UseQueryResult<AgreementReport> =>
  useQuery(agreementQuery(useWorkspaceKeys(), group));

const modelVersionsQuery = (keys: WorkspaceKeys): WorkspaceQuery<ModelVersionsOverview> => ({
  ...workspaceQuery([...keys.all, "model-versions"], () => researchApi.admin.listModelVersions()),
  refetchOnWindowFocus: "always",
});

const benchmarkQuery = (
  keys: WorkspaceKeys,
  group: AnalysisGroup,
): WorkspaceQuery<BenchmarkReport> => ({
  ...workspaceQuery([...keys.all, "benchmark", group], () => researchApi.admin.getBenchmark(group)),
  refetchOnWindowFocus: "always",
});

export const useModelVersions = (): UseQueryResult<ModelVersionsOverview> =>
  useQuery(modelVersionsQuery(useWorkspaceKeys()));

export const useCreateModelVersion = (): UseMutationResult<
  ModelVersion,
  Error,
  ModelVersionInput
> =>
  useWorkspaceMutation({
    mutationFn: (input: ModelVersionInput) => researchApi.admin.createModelVersion(input),
  });

export const useUpdateModelVersion = (): UseMutationResult<
  ModelVersion,
  Error,
  { id: string; input: ModelVersionUpdate }
> =>
  useWorkspaceMutation({
    mutationFn: ({ id, input }: { id: string; input: ModelVersionUpdate }) =>
      researchApi.admin.updateModelVersion(id, input),
  });

export const useBenchmark = (group: AnalysisGroup): UseQueryResult<BenchmarkReport> =>
  useQuery(benchmarkQuery(useWorkspaceKeys(), group));

export const useStartBenchmark = (): UseMutationResult<BenchmarkQueued, Error, BenchmarkInput> =>
  useWorkspaceMutation({
    mutationFn: (input: BenchmarkInput) => researchApi.admin.startBenchmark(input),
  });

/** The route accepts twelve steps; older benchmark versions can make a group larger. */
export const useGateBenchmark = (): UseMutationResult<PipelineStep[], Error, BenchmarkGateInput> =>
  useWorkspaceMutation({
    mutationFn: async ({ group, stepIds }: BenchmarkGateInput) => {
      const steps: PipelineStep[] = [];
      for (let index = 0; index < stepIds.length; index += 12) {
        steps.push(
          ...(await researchApi.admin.gateBenchmark({
            group,
            stepIds: stepIds.slice(index, index + 12),
          })),
        );
      }
      return steps;
    },
  });
